import React, { useState, useEffect, useRef } from 'react';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { useNavigate } from 'react-router-dom';
import { CHATBOT_CONFIG } from './chatbotConfig';
import './Chatbot.css';

const Chatbot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const { customer, loading: authLoading } = useCustomerAuth();
    const navigate = useNavigate();
    const messagesEndRef = useRef(null);

    // Initial Greeting
    useEffect(() => {
        if (!history.length) {
            setHistory([{
                type: 'bot',
                text: CHATBOT_CONFIG.intents.HOME.text,
                options: CHATBOT_CONFIG.intents.HOME.options
            }]);
        }
    }, [history.length]);

    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [history, isOpen, loading]);

    // Mobile Scroll Lock
    useEffect(() => {
        if (isOpen && window.innerWidth <= 768) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        // Cleanup
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    const addBotMessage = (message, delay = 250) => {
        setTimeout(() => {
            setLoading(false);
            setHistory(prev => [...prev, {
                type: 'bot',
                text: message.text,
                options: message.options,
                cards: message.cards || null
            }]);

            // Handle Follow-up
            if (message.followUp) {
                const followUpIntent = CHATBOT_CONFIG.intents[message.followUp];
                setTimeout(() => {
                    setHistory(prev => [...prev, {
                        type: 'bot',
                        text: followUpIntent.text,
                        options: followUpIntent.options
                    }]);
                }, 800); // Small delay after main message
            }

        }, delay);
    };

    const handleOptionClick = async (option) => {
        // User Message Bubble
        setHistory(prev => [...prev, { type: 'user', text: option.label }]);

        // Handle URL Navigation
        if (option.url) {
            if (option.external) {
                window.open(option.url, '_blank');
            } else {
                navigate(option.url);
                setIsOpen(false);
            }
            return;
        }

        // Handle Next Intent
        if (option.next) {
            const nextIntent = CHATBOT_CONFIG.intents[option.next];
            setLoading(true); // Show typing indicator

            // Check for smart actions
            if (nextIntent.action === 'CHECK_AUTH_AND_FETCH_ORDERS') {
                await handleTrackOrder(nextIntent);
            } else {
                // Static Response with Delay
                addBotMessage(nextIntent);
            }
        }
    };

    const handleTrackOrder = async (intentConfig) => {
        // 1. Check Auth
        if (!customer) {
            addBotMessage({
                text: "You need to be logged in to track your orders.",
                options: [
                    { label: "Login Now", url: `/login?redirect=${encodeURIComponent('/account/orders')}` },
                    { label: "« Back to Menu", next: "HOME" }
                ]
            }, 600);
            return;
        }

        // 2. Fetch Orders
        try {
            const BASE_URL = process.env.REACT_APP_API_BASE || 'https://handmade-haven.in';
            const res = await fetch(`${BASE_URL}/api/customer/orders.php`, {
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include' // Important for session
            });
            const data = await res.json();

            if (data.ok && data.orders && data.orders.length > 0) {
                // Show recent 3 orders
                const recentOrders = data.orders.slice(0, 3);

                // Create custom options for orders
                const orderOptions = recentOrders.map(order => ({
                    label: `Order #${order.id} (${order.status})`,
                    url: `/account/orders/${order.id}`
                }));

                orderOptions.push({ label: "View All Orders", url: "/account/orders" });
                orderOptions.push({ label: "« Back to Menu", next: "HOME" });

                addBotMessage({
                    text: "Here are your recent orders:",
                    options: orderOptions
                });
            } else {
                addBotMessage({
                    text: "I couldn't find any recent orders.",
                    options: [
                        { label: "Start Shopping", url: "/" },
                        { label: "« Back to Menu", next: "HOME" }
                    ]
                });
            }

        } catch (err) {
            addBotMessage({
                text: "Sorry, I had trouble fetching your orders. Please try checking your account manually.",
                options: [
                    { label: "Go to My Account", url: "/account" },
                    { label: "« Back to Menu", next: "HOME" }
                ]
            });
        }
    };

    // Card Renderer Component
    const FAQCard = ({ card }) => {
        const [expanded, setExpanded] = useState(false);
        return (
            <div className="faq-card" onClick={() => setExpanded(!expanded)}>
                <div className="faq-question">
                    {card.question}
                    <span className={`arrow ${expanded ? 'open' : ''}`}>▼</span>
                </div>
                {expanded && <div className="faq-answer">{card.answer}</div>}
            </div>
        );
    };

    return (
        <div className="chatbot-wrapper">
            {/* Chat Trigger Button */}
            <button
                className={`chatbot-trigger ${isOpen ? 'active' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Chat Support"
            >
                {isOpen ? '✕' : '💬'}
            </button>

            {/* Chat Modal */}
            <div className={`chatbot-modal ${isOpen ? 'open' : ''}`}>
                <div className="chatbot-header">
                    <h3>Handmade Helper ✨</h3>
                    <button onClick={() => setIsOpen(false)}>✕</button>
                </div>

                <div className="chatbot-messages">
                    {history.map((msg, idx) => (
                        <div key={idx} className={`chat-bubble ${msg.type}`}>
                            {msg.type === 'bot' && <div className="bot-avatar">🤖</div>}
                            <div className="bubble-content">
                                {msg.text && <div className="message-text">{msg.text}</div>}

                                {/* FAQ Cards Rendering */}
                                {msg.cards && (
                                    <div className="chat-cards">
                                        {msg.cards.map((card, cIdx) => (
                                            <FAQCard key={cIdx} card={card} />
                                        ))}
                                    </div>
                                )}

                                {/* Options Rendering */}
                                {msg.options && (
                                    <div className="chat-options">
                                        {msg.options.map((opt, oIdx) => (
                                            <button
                                                key={oIdx}
                                                onClick={() => handleOptionClick(opt)}
                                                className="option-btn"
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                    {loading && (
                        <div className="chat-bubble bot">
                            <div className="bot-avatar">🤖</div>
                            <div className="bubble-content typing-indicator">
                                <span>●</span><span>●</span><span>●</span>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>
            </div>
        </div>
    );
};

export default Chatbot;
